// PDF Export Service
// Professional PDF generation with Vibality branding

import jsPDF from 'jspdf';

class PDFExportService {
  constructor() {
    this.colors = {
      primary: '#FF6B35',      // Vibality orange
      secondary: '#2563EB',    // Blue
      dark: '#1F2937',         // Dark gray
      light: '#F3F4F6',        // Light gray
      text: '#374151',         // Text gray
      accent: '#10B981'        // Green
    };

    this.fonts = {
      title: 22,
      heading: 16,
      subheading: 14,
      body: 11,
      small: 9
    };
  }

  // Generate professional Reddit insights PDF
  async generateRedditInsightsPDF(analysis, marketContext) {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPosition = 20;

    // Add Vibality branding header
    yPosition = this.addHeader(pdf, yPosition);

    // Add title and context
    yPosition = this.addTitle(pdf, 'Reddit Market Insights Report', yPosition);
    yPosition = this.addContext(pdf, marketContext, yPosition);

    // Executive Summary
    if (analysis.summary) {
      yPosition = this.addSection(pdf, 'Executive Summary', analysis.summary, yPosition);
    }

    // Key Pain Points
    if (analysis.keyPainPoints) {
      yPosition = this.addSection(pdf, 'Key Pain Points', analysis.keyPainPoints, yPosition);
    }

    // Check if we need a new page
    if (yPosition > 240) {
      pdf.addPage();
      yPosition = 20;
    }

    // Demographics
    if (analysis.demographics) {
      yPosition = this.addSection(pdf, 'User Demographics', analysis.demographics, yPosition);
    }

    // Market Opportunities
    if (analysis.opportunities) {
      yPosition = this.addSection(pdf, 'Market Opportunities', analysis.opportunities, yPosition);
    }

    // Notable Quotes
    if (analysis.notableQuotes) {
      yPosition = this.addQuotesSection(pdf, analysis.notableQuotes, yPosition);
    }

    // Add footer
    this.addFooter(pdf);

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `vibality-reddit-insights-${timestamp}.pdf`;

    return {
      pdf,
      filename,
      download: () => pdf.save(filename)
    };
  }

  // Add Vibality header with logo and branding
  addHeader(pdf, yPosition) {
    // Background header bar
    pdf.setFillColor(this.hexToRgb(this.colors.primary));
    pdf.rect(0, 0, 210, 25, 'F');

    // Company name/logo placeholder (you can replace with actual logo)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Vibality', 15, 16);

    // Tagline
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text('Market Discovery & Insights', 15, 21);

    // Report date
    const reportDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const dateWidth = pdf.getTextWidth(reportDate);
    pdf.text(reportDate, 210 - 15 - dateWidth, 16);

    return yPosition + 15;
  }

  // Add main title
  addTitle(pdf, title, yPosition) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(this.fonts.title);
    pdf.setTextColor(this.hexToRgb(this.colors.dark));
    pdf.text(title, 15, yPosition);

    // Add underline
    const titleWidth = pdf.getTextWidth(title);
    pdf.setDrawColor(this.hexToRgb(this.colors.primary));
    pdf.setLineWidth(0.8);
    pdf.line(15, yPosition + 2, 15 + titleWidth, yPosition + 2);

    return yPosition + 15;
  }

  // Add market context
  addContext(pdf, marketContext, yPosition) {
    if (!marketContext) return yPosition;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(this.fonts.subheading);
    pdf.setTextColor(this.hexToRgb(this.colors.secondary));
    pdf.text('Market Context:', 15, yPosition);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(this.fonts.body);
    pdf.setTextColor(this.hexToRgb(this.colors.text));

    const contextText = this.wrapText(pdf, marketContext, 180);
    const lines = contextText.split('\n');

    let currentY = yPosition + 6;
    lines.forEach(line => {
      pdf.text(line, 15, currentY);
      currentY += 5;
    });

    return currentY + 8;
  }

  // Add a section with heading and content
  addSection(pdf, heading, content, yPosition) {
    // Check if we need a new page
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }

    // Section heading
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(this.fonts.heading);
    pdf.setTextColor(this.hexToRgb(this.colors.primary));
    pdf.text(heading, 15, yPosition);

    // Section content
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(this.fonts.body);
    pdf.setTextColor(this.hexToRgb(this.colors.text));

    const cleanContent = this.cleanTextForPDF(content);
    const wrappedText = this.wrapText(pdf, cleanContent, 180);
    const lines = wrappedText.split('\n');

    let currentY = yPosition + 8;
    lines.forEach(line => {
      // Check if we need a new page
      if (currentY > 270) {
        pdf.addPage();
        currentY = 20;
      }

      pdf.text(line, 15, currentY);
      currentY += 5;
    });

    return currentY + 10;
  }

  // Add quotes section with special formatting
  addQuotesSection(pdf, quotes, yPosition) {
    // Check if we need a new page
    if (yPosition > 220) {
      pdf.addPage();
      yPosition = 20;
    }

    // Section heading
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(this.fonts.heading);
    pdf.setTextColor(this.hexToRgb(this.colors.primary));
    pdf.text('Notable User Quotes', 15, yPosition);

    let currentY = yPosition + 8;

    // Extract individual quotes
    const cleanQuotes = this.cleanTextForPDF(quotes);
    const quoteLines = cleanQuotes.split('\n').filter(line =>
      line.trim().length > 10 &&
      (line.includes('"') || line.includes('said') || line.includes('user'))
    );

    quoteLines.slice(0, 5).forEach((quote, index) => {
      // Check if we need a new page
      if (currentY > 260) {
        pdf.addPage();
        currentY = 20;
      }

      // Quote background
      pdf.setFillColor(250, 250, 250);
      const quoteHeight = Math.max(15, Math.ceil(quote.length / 80) * 6);
      pdf.rect(15, currentY - 4, 180, quoteHeight, 'F');

      // Quote icon
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(this.hexToRgb(this.colors.secondary));
      pdf.text('"', 20, currentY + 2);

      // Quote text
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(this.fonts.body);
      pdf.setTextColor(this.hexToRgb(this.colors.text));

      const wrappedQuote = this.wrapText(pdf, quote.replace(/^["']|["']$/g, ''), 160);
      const quoteTextLines = wrappedQuote.split('\n');

      quoteTextLines.forEach((line, lineIndex) => {
        pdf.text(line, 28, currentY + (lineIndex * 5));
      });

      // Attribution
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(this.fonts.small);
      pdf.setTextColor(this.hexToRgb(this.colors.secondary));
      pdf.text('— Reddit User', 28, currentY + (quoteTextLines.length * 5) + 3);

      currentY += quoteHeight + 8;
    });

    return currentY + 5;
  }

  // Add footer
  addFooter(pdf) {
    const pageCount = pdf.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);

      // Footer line
      pdf.setDrawColor(this.hexToRgb(this.colors.light));
      pdf.setLineWidth(0.5);
      pdf.line(15, 285, 195, 285);

      // Page number
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(this.fonts.small);
      pdf.setTextColor(this.hexToRgb(this.colors.text));
      pdf.text(`Page ${i} of ${pageCount}`, 15, 290);

      // Company info
      pdf.text('Generated by Vibality Market Discovery Platform', 195 - pdf.getTextWidth('Generated by Vibality Market Discovery Platform'), 290);
    }
  }

  // Utility functions
  hexToRgb(hex) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  }

  wrapText(pdf, text, maxWidth) {
    const words = text.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const testWidth = pdf.getTextWidth(testLine);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.join('\n');
  }

  cleanTextForPDF(text) {
    if (!text) return '';

    return text
      // Remove HTML tags
      .replace(/<[^>]*>/g, '')
      // Remove any remaining markdown
      .replace(/\*+/g, '')
      .replace(/_+/g, '')
      .replace(/#+/g, '')
      // Clean up bullet points
      .replace(/^[\s]*[-•*]\s*/gm, '• ')
      // Clean up whitespace
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n')
      .trim();
  }
}

export default PDFExportService;